/**
 * Online play (docs/architecture.md §7, docs/game-design.md §6). All placeholders until the M3
 * playtest measures real connections.
 */
import type { BotDifficulty } from './bot';

export const ONLINE = {
  /** A press applies this many ticks after it is made (2 ticks = 33 ms), so it reaches the other side sooner. */
  inputDelay: 2,
  /** Each input message repeats this many of the client's latest inputs (late or lost packets). */
  resendInputs: 8,
  /**
   * Ticks per network message, both ways (D22): the server sends the frames of two ticks at once
   * and a client its inputs of two ticks, which halves the messages the small server handles.
   */
  framesPerMessage: 2, // placeholder
  inputsPerMessage: 2, // placeholder
  /** Inputs further ahead of the server than this (ticks) are rejected as bogus. */
  maxInputsAhead: 90,
  /** The server sends a state hash every this many ticks; a mismatch triggers a full resync. */
  hashEvery: 10,
  /** A client predicts at most this many ticks past its last confirmed tick, then waits. */
  maxPrediction: 30,
  /** Clients aim to deliver each input this many ticks before the server needs it. */
  targetSlack: 2,
  /** Seconds between clock pings (the first few come faster). */
  pingSeconds: 1,
  /**
   * Seconds a player who is away (connection dropped, page reloaded) has to come back before
   * forfeiting; the fight is paused meanwhile (D19, docs/architecture.md §7 "Disconnects").
   */
  reconnectSeconds: 30,
  /** Client: with no word from the server for this long, the fight shows "Reconnecting…" (s). */
  stallSeconds: 1.2, // placeholder
  /** Client: after this long without a word, the connection is dropped and made again (s). */
  silentSeconds: 4, // placeholder
  /** Client: how long the reconnection keeps trying before the fight counts as lost (s). */
  giveUpSeconds: 40, // placeholder
  /** HUD ping colours: good below, fair below, poor from (ms). */
  pingGoodMs: 80,
  pingFairMs: 150,
  /** After both fighters are ready (or the ready timeout), tick 1 comes this much later (s). */
  startDelay: 0.8,
  /** Seconds to wait for both clients to load the arena before starting anyway. */
  readyTimeout: 10,

  matchmaking: {
    /** Seconds per matchmaking cycle. */
    cycleSeconds: 1,
    /** Hidden rating window: ±start at first, widening per second waited. */
    windowStart: 100,
    windowPerSecond: 50,
    /**
     * A bot fills in after this many seconds without a human opponent (docs/game-design.md §6).
     * From the second before, any waiting human is taken whatever the gap: a human beats a bot (D17).
     */
    botAfterSeconds: 20,
    /** Bot difficulty from the player's hidden rating: easy below, hard from. */
    botEasyBelow: 900,
    botHardFrom: 1150,
  },
} as const;

export function botDifficultyFor(rating: number): BotDifficulty {
  const m = ONLINE.matchmaking;
  return rating >= m.botHardFrom ? 'hard' : rating < m.botEasyBelow ? 'easy' : 'normal';
}
